import classNames from 'clsx';
import { kebabCase } from 'lodash';
import type { ChangeEventHandler } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import type { FieldMetaState } from 'react-final-form';

import Arrow from '@swf/core/assets/icons/arrow.svg';

import styles from './DropDown.scss';
import type { DropDownOptionText, DropDownOptionValue } from './DropDownOption';
import DropDownOption from './DropDownOption';

export interface Props {
  options: SelectOption[];
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string | React.ReactNode;
  value?: SelectOptionValue;
  error?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: FieldMetaState<any>;
  onFocus?(event: React.FocusEvent<HTMLSelectElement>): void;
  onSelect?(option: SelectOption): void;
}

export interface SelectOption {
  readonly text: DropDownOptionText;
  readonly value?: DropDownOptionValue;
  readonly data?: string;
}

type SelectOptionValue = string | number | SelectOption;

const findSelectedOption = (
  options: SelectOption[],
  value?: SelectOptionValue
): SelectOption | undefined => {
  if (value !== undefined) {
    return options.find((option) => {
      if (option.value === 0 && value === '') {
        return false;
      }
      // eslint-disable-next-line eqeqeq
      return option.value == value;
    });
  }
  return undefined;
};

const DropDown: React.FC<Props> = ({
  id,
  placeholder = '',
  value = '',
  options = [],
  disabled,
  meta,
  className,
  error,
  onSelect,
  onFocus
}) => {
  const [selectedOption, setSelectedOption] = useState(findSelectedOption(options, value));
  const singleOption = options.length === 1;
  const valid = meta?.dirty && meta.valid;
  const invalid = error || ((meta?.touched || meta?.dirty) && meta.invalid);
  const showArrow = !singleOption && !disabled;
  const selectedValue = selectedOption?.value;
  const showPlaceholderOption = options.length > 1 && !selectedOption;

  const optionSelected: ChangeEventHandler<HTMLSelectElement> = useCallback(
    (event) => {
      const selectedOption = findSelectedOption(options, event.target.value);
      setSelectedOption(selectedOption);
      if (onSelect && selectedOption) {
        onSelect(selectedOption);
      }
    },
    [onSelect, options]
  );

  useEffect(() => {
    setSelectedOption(findSelectedOption(options, value));
  }, [value, options]);

  useEffect(() => {
    if (singleOption && onSelect) {
      onSelect(options[0]);
    }
  }, [value, options, singleOption, onSelect]);

  return (
    <div className={classNames(className, styles.selectorWrapper)}>
      {showArrow && (
        <Arrow
          className={classNames(styles.icon, {
            [styles.error]: invalid,
            [styles.valid]: valid
          })}
        />
      )}
      <select
        id={id}
        value={selectedValue || selectedValue === 0 ? selectedValue : ''}
        className={classNames(styles.selector, {
          [styles.disabled]: disabled,
          [styles.singleOption]: singleOption,
          [styles.error]: invalid,
          [styles.valid]: valid
        })}
        disabled={disabled}
        onChange={optionSelected}
        onFocus={onFocus}
        data-testid={kebabCase(id)}
      >
        {showPlaceholderOption && (
          <DropDownOption className={styles.placeholder} text={placeholder} value="" />
        )}
        {options.map((option, index) => (
          <DropDownOption
            className={styles.option}
            text={option.text}
            value={option.value}
            key={index}
          />
        ))}
      </select>
    </div>
  );
};

export default DropDown;
